import {
    createContext,
    PropsWithChildren,
    startTransition,
    useCallback,
    useMemo,
    useState,
} from "react";
import { UrlUpdateType } from "use-query-params";
import { useInvoices } from "../../api/invoice.api";
import { Invoice, InvoiceSortValue } from "../../common/types/invoice";
import { useInvoicesPageSortingCache } from "../../hooks/useInvoicesPageSortingCache";
import { useInvoicesView } from "../../contexts/InvoicesViewContext";
import { InvoiceFilters } from "../../common/contracts/invoice.contract";
import { useSort, UseSort } from "./useSort";
const PER_PAGE = 25;

export interface PaginatedInvoicesTableContextValue {
    currentPage: number;
    totalPages: number;
    invoiceCount: number;
    setPage:
        | React.Dispatch<React.SetStateAction<number>>
        | ((
              newValue: number | null | undefined,
              updateType?: UrlUpdateType,
          ) => void);
    handleSortChange: UseSort["handleSortChange"];
    currentSort: UseSort["currentSort"];
    invoices?: Invoice[];
    isLoading: boolean;
}

const defaultValue: PaginatedInvoicesTableContextValue = {
    currentPage: 0,
    totalPages: 0,
    invoiceCount: 0,
    setPage: () => {},
    handleSortChange: () => {},
    currentSort: InvoiceSortValue.NONE,
    isLoading: false,
};

export const PaginatedInvoicesTableContext =
    createContext<PaginatedInvoicesTableContextValue>(defaultValue);

interface Props {
    overlayFilters?: InvoiceFilters;
}

export function PaginatedInvoicesTableContextProvider({
    children,
    overlayFilters,
}: PropsWithChildren<Props>) {
    const [cachedSorting, setCachedSorting] = useInvoicesPageSortingCache();
    const { filters } = useInvoicesView();
    const [page, setPage] = useState(1);

    const { sortExpression, currentSort, handleSortChange } =
        useSort(cachedSorting);

    const invoicesResult = useInvoices({
        filters: {
            ...filters,
            ...overlayFilters,
        },
        sort: sortExpression,
        page,
        limit: PER_PAGE,
    });

    const { data: invoices, isLoading } = invoicesResult;

    const handleSortChangeWithPageReset = useCallback(
        (value: InvoiceSortValue) => {
            startTransition(() => {
                handleSortChange(value);
                setCachedSorting(value);
                setPage(1);
            });
        },
        [handleSortChange, setCachedSorting, setPage],
    );

    const contextValue = useMemo<PaginatedInvoicesTableContextValue>(
        () => ({
            currentPage: page,
            totalPages: invoices?.pageCount ?? 1,
            invoiceCount: invoices?.total ?? 0,
            invoices: invoices?.data,
            setPage,
            handleSortChange: handleSortChangeWithPageReset,
            currentSort,
            isLoading,
        }),
        [page, invoices, handleSortChangeWithPageReset, currentSort, isLoading],
    );

    return (
        <PaginatedInvoicesTableContext.Provider value={contextValue}>
            {children}
        </PaginatedInvoicesTableContext.Provider>
    );
}
